import React, { useState, useEffect } from 'react';
import { useBooks } from '../context/BookContext';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { Palette } from 'lucide-react';
import { WOOD_THEMES, DEFAULT_WOOD_THEME_KEY } from '../themes/woodThemes';

// Weather Overlay Components
const RainOverlay = () => (
  <div style={weatherOverlayStyle}>
    <svg width="100%" height="100%">
      <defs>
        <style>
          {`
            @keyframes rainFall {
              0% { transform: translateY(-50px); opacity: 0; }
              50% { opacity: 0.4; }
              100% { transform: translateY(200px); opacity: 0; }
            }
            .rain-drop {
              fill: none;
              stroke: #a2c4c9;
              stroke-width: 1.5;
              stroke-linecap: round;
              animation: rainFall 1.5s infinite linear;
            }
          `}
        </style>
      </defs>
      <line className="rain-drop" x1="15%" y1="0" x2="15%" y2="12" style={{ animationDelay: '0s', animationDuration: '1.2s' }} />
      <line className="rain-drop" x1="30%" y1="0" x2="30%" y2="12" style={{ animationDelay: '0.4s', animationDuration: '1s' }} />
      <line className="rain-drop" x1="45%" y1="0" x2="45%" y2="12" style={{ animationDelay: '0.1s', animationDuration: '1.4s' }} />
      <line className="rain-drop" x1="60%" y1="0" x2="60%" y2="12" style={{ animationDelay: '0.6s', animationDuration: '1.1s' }} />
      <line className="rain-drop" x1="75%" y1="0" x2="75%" y2="12" style={{ animationDelay: '0.2s', animationDuration: '1.3s' }} />
      <line className="rain-drop" x1="90%" y1="0" x2="90%" y2="12" style={{ animationDelay: '0.8s', animationDuration: '1s' }} />
    </svg>
  </div>
);

const CozyOverlay = () => (
  <div style={weatherOverlayStyle}>
    <div style={sunbeamStyle} />
    <svg width="100%" height="100%">
      <defs>
        <style>
          {`
            @keyframes floatDust {
              0% { transform: translateY(120px) translateX(0); opacity: 0; }
              20% { opacity: 0.5; }
              80% { opacity: 0.5; }
              100% { transform: translateY(-30px) translateX(15px); opacity: 0; }
            }
            .dust-mote {
              fill: #ffd54f;
              opacity: 0.5;
              animation: floatDust 8s infinite linear;
            }
          `}
        </style>
      </defs>
      <circle className="dust-mote" cx="20%" cy="0" r="1.5" style={{ animationDelay: '0s', animationDuration: '7s' }} />
      <circle className="dust-mote" cx="40%" cy="0" r="1" style={{ animationDelay: '2s', animationDuration: '9s' }} />
      <circle className="dust-mote" cx="55%" cy="0" r="2" style={{ animationDelay: '1s', animationDuration: '6s' }} />
      <circle className="dust-mote" cx="75%" cy="0" r="1.5" style={{ animationDelay: '4s', animationDuration: '8s' }} />
      <circle className="dust-mote" cx="85%" cy="0" r="1" style={{ animationDelay: '3s', animationDuration: '10s' }} />
    </svg>
  </div>
);

const MistOverlay = () => (
  <div style={{ ...weatherOverlayStyle, background: 'linear-gradient(to top, rgba(144, 164, 174, 0.15) 0%, rgba(0,0,0,0) 80%)' }}>
    <svg width="100%" height="100%">
      <defs>
        <style>
          {`
            @keyframes driftFog {
              0% { transform: translateX(-30px); opacity: 0.15; }
              50% { transform: translateX(30px); opacity: 0.35; }
              100% { transform: translateX(-30px); opacity: 0.15; }
            }
            .fog-cloud {
              fill: #eceff1;
              opacity: 0.25;
              animation: driftFog 15s infinite ease-in-out;
            }
          `}
        </style>
        <filter id="fog-blur">
          <feGaussianBlur stdDeviation="8" />
        </filter>
      </defs>
      <ellipse className="fog-cloud" cx="30%" cy="80%" rx="80" ry="25" filter="url(#fog-blur)" />
      <ellipse className="fog-cloud" cx="70%" cy="85%" rx="100" ry="30" filter="url(#fog-blur)" style={{ animationDelay: '3s', animationDuration: '18s' }} />
    </svg>
  </div>
);

const ThrillingOverlay = () => (
  <div style={weatherOverlayStyle}>
    <svg width="100%" height="100%">
      <defs>
        <style>
          {`
            @keyframes lightningFlash {
              0%, 94%, 96%, 100% { opacity: 0; }
              95%, 97% { opacity: 0.2; }
            }
            .flash-bg {
              fill: #ffffff;
              animation: lightningFlash 6s infinite ease-in-out;
            }
          `}
        </style>
      </defs>
      <rect className="flash-bg" x="0" y="0" width="100%" height="100%" />
    </svg>
  </div>
);

const getDecorEmoji = (name) => {
  if (!name) return '📦';
  const n = name.toLowerCase();
  if (n.includes('fire')) return '🔥';
  if (n.includes('rug')) return '🧎';
  if (n.includes('lamp')) return '💡';
  if (n.includes('cat')) return '🐱';
  if (n.includes('candle') || n.includes('candlestick')) return '🕯️';
  if (n.includes('plant') || n.includes('ivy') || n.includes('fern') || n.includes('willow') || n.includes('greenhouse')) return '🪴';
  if (n.includes('teapot')) return '🫖';
  if (n.includes('shelf') || n.includes('ladder') || n.includes('art')) return '📚';
  if (n.includes('mug') || n.includes('tea') || n.includes('steaming')) return '☕';
  if (n.includes('owl')) return '🦉';
  if (n.includes('glass') || n.includes('jar') || n.includes('lights')) return '🔮';
  if (n.includes('terrarium') || n.includes('moss')) return '🧪';
  if (n.includes('skillet') || n.includes('pan')) return '🍳';
  return '🎁';
};



// Cozy composite book spine generator helpers
const getSpineProperties = (book) => {
  const id = book.id || 'placeholder';
  // Deterministic hash based on book ID
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  // 1. Spine profiles (respect trophyStyle overrides)
  const styles = ['classic', 'vintage', 'modern', 'gothic', 'journal'];
  let style = styles[hash % styles.length];
  if (book.trophyStyle) {
    if (book.trophyStyle === 'leather') style = 'vintage';
    else if (book.trophyStyle === 'gold-leaf') style = 'gothic';
    else if (book.trophyStyle === 'linen') style = 'classic';
    else if (book.trophyStyle === 'paperback') style = 'modern';
  }
  
  // 2. Spine heights & widths
  const height = 80 + (hash % 16); // 80% to 95% of shelf height
  const width = 28 + (hash % 15);  // 28px to 42px width
  
  // 3. Cozy Palette Colors (respect spineColor overrides)
  const palette = [
    { bg: '#5b7065', text: '#eae1d8', accent: '#d4af37', label: 'Sage' },
    { bg: '#a37070', text: '#fbf7f4', accent: '#e8cb75', label: 'Dusty Rose' },
    { bg: '#2b3a4a', text: '#e7d8c9', accent: '#d4af37', label: 'Midnight Navy' },
    { bg: '#602c3c', text: '#eae1d8', accent: '#e2be5a', label: 'Burgundy' },
    { bg: '#b28b43', text: '#3c2a1a', accent: '#eae1d8', label: 'Mustard Gold' },
    { bg: '#422c21', text: '#e5d1b8', accent: '#dca842', label: 'Leather Brown' },
    { bg: '#1c362d', text: '#f3eae1', accent: '#d0ad52', label: 'Forest Green' },
    { bg: '#1e4850', text: '#f9f6f0', accent: '#eec25e', label: 'Deep Teal' }
  ];
  let color = palette[hash % palette.length];
  if (book.spineColor) {
    color = {
      bg: book.spineColor,
      text: '#eae1d8',
      accent: book.trophyStyle === 'gold-leaf' ? '#ffd700' : '#e8cb75',
      label: 'Custom'
    };
  }
  
  // 4. Logo / Embossing
  const embossingTypes = ['stars', 'leaves', 'diamond', 'crest', 'lines'];
  const embossing = embossingTypes[hash % embossingTypes.length];
  
  // 5. Title alignment (top or middle)
  const titleAlign = hash % 2 === 0 ? 'center' : 'flex-start';
  
  return {
    style,
    height: `${height}%`,
    width: `${width}px`,
    color,
    embossing,
    titleAlign
  };
};

const getSpineDecoration = (spineProp) => {
  const { style, color } = spineProp;
  
  const lineStyle = {
    position: 'absolute',
    left: '3px',
    right: '3px',
    height: '1.5px',
    backgroundColor: color.accent,
    opacity: 0.6,
    boxShadow: '0 0.5px 1px rgba(0,0,0,0.2)'
  };
  
  if (style === 'classic') {
    return (
      <>
        <div style={{ ...lineStyle, top: '8px' }} />
        <div style={{ ...lineStyle, top: '11px' }} />
        <div style={{ ...lineStyle, bottom: '8px' }} />
        <div style={{ ...lineStyle, bottom: '11px' }} />
      </>
    );
  }
  
  if (style === 'vintage') {
    return (
      <>
        {/* Horizontal structural ridges */}
        <div style={{ position: 'absolute', top: '25%', left: 0, right: 0, height: '3px', backgroundColor: 'rgba(0,0,0,0.3)', borderTop: `1.5px solid ${color.accent}`, opacity: 0.8 }} />
        <div style={{ position: 'absolute', top: '55%', left: 0, right: 0, height: '3px', backgroundColor: 'rgba(0,0,0,0.3)', borderTop: `1.5px solid ${color.accent}`, opacity: 0.8 }} />
        <div style={{ position: 'absolute', bottom: '25%', left: 0, right: 0, height: '3px', backgroundColor: 'rgba(0,0,0,0.3)', borderTop: `1.5px solid ${color.accent}`, opacity: 0.8 }} />
      </>
    );
  }
  
  if (style === 'gothic') {
    return (
      <>
        {/* Pointed top arch via border radius & gold frame border */}
        <div style={{ position: 'absolute', top: '6px', left: '4px', right: '4px', height: '14px', border: `1px solid ${color.accent}`, borderRadius: '4px 4px 0 0', borderBottom: 'none', opacity: 0.5 }} />
        <div style={{ position: 'absolute', bottom: '6px', left: '4px', right: '4px', height: '14px', border: `1px solid ${color.accent}`, borderRadius: '0 0 4px 4px', borderTop: 'none', opacity: 0.5 }} />
      </>
    );
  }
  
  if (style === 'journal') {
    return (
      <>
        {/* Left-side binding spine cloth overlay */}
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '6px', background: 'rgba(0,0,0,0.3)', borderRight: '1px solid rgba(255,255,255,0.1)' }} />
        {/* Cute hanging bookmark ribbon */}
        <div style={{ position: 'absolute', bottom: '-10px', left: '45%', width: '4px', height: '14px', backgroundColor: '#bf5a5a', borderRadius: '0 0 2px 2px', boxShadow: '0 1px 3px rgba(0,0,0,0.35)', zIndex: -1 }} />
      </>
    );
  }
  
  return null; // Modern profile has no extra structural elements
};

const getEmbossingLogo = (spineProp) => {
  const { embossing, color } = spineProp;
  const style = {
    color: color.accent,
    fontSize: '0.85rem', // Bolder and larger size
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.95, // High contrast
    pointerEvents: 'none',
    lineHeight: 1,
    filter: 'drop-shadow(0 1px 1.5px rgba(0,0,0,0.45))' // Soft metallic drop shadow
  };

  switch (embossing) {
    case 'stars':
      return <span style={style}>✦</span>;
    case 'leaves':
      return <span style={style}>🍃</span>;
    case 'diamond':
      return <span style={style}>❖</span>;
    case 'crest':
      return <span style={style}>⚜</span>;
    case 'lines':
      return <span style={{ ...style, border: `1.5px solid ${color.accent}`, width: '8px', height: '8px', borderRadius: '50%' }}></span>;
    default:
      return null;
  }
};

const getInscribedTitle = (book, spineProp) => {
  const { color, style } = spineProp;
  
  const font = style === 'classic' || style === 'vintage' || style === 'gothic' 
    ? "'Playfair Display', serif" 
    : "'Outfit', sans-serif";

  const cleanTitle = book.title.split(':')[0].substring(0, 16);

  // Determine if this spine profile has an elegant vertical paper label box
  // Paper labels look extremely clean, cozy, and make wording 100% legible!
  const hasLabel = style === 'classic' || style === 'vintage' || style === 'journal';

  if (hasLabel) {
    const labelStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#faf6eb', // warm cream paper
      border: style === 'classic' ? '1px solid #d4af37' : '1.5px solid rgba(0,0,0,0.15)',
      borderRadius: '3px',
      padding: '8px 2.5px',
      margin: '6px 4px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2), inset 0 0 4px rgba(0,0,0,0.05)',
      maxHeight: '115px',
      boxSizing: 'border-box',
      writingMode: 'vertical-rl',
      transform: 'rotate(180deg)',
      pointerEvents: 'none'
    };

    const textStyle = {
      fontFamily: font,
      fontSize: '0.72rem',
      fontWeight: '700',
      color: '#2e1f15', // crisp dark charcoal/leather color
      letterSpacing: '0.4px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      lineHeight: '1.1'
    };

    return (
      <div style={labelStyle}>
        <div style={textStyle}>{cleanTitle}</div>
      </div>
    );
  }

  // Modern and gothic styles keep direct spine printing but with significantly increased contrast and size!
  const textStyle = {
    fontFamily: font,
    fontSize: '0.74rem',
    fontWeight: '700',
    color: '#ffffff', // pure white for maximum legibility
    textShadow: '0 1px 2px rgba(0,0,0,0.75), 0 0 4px rgba(0,0,0,0.4)',
    letterSpacing: '0.5px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxHeight: '110px',
    textAlign: 'center',
    padding: '4px 0',
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    pointerEvents: 'none',
    opacity: 0.95
  };

  return (
    <div style={textStyle}>
      {cleanTitle}
    </div>
  );
};

const CozyBookSpine = ({ book, onClick }) => {
  const spineProp = getSpineProperties(book);
  const { style, height, width, color, titleAlign } = spineProp;

  // 3D Cylinder / Round book spine gradient
  const gradientBg = `linear-gradient(to right, 
    rgba(0, 0, 0, 0.45) 0%, 
    rgba(255, 255, 255, 0.22) 18%, 
    ${color.bg} 35%, 
    ${color.bg} 80%, 
    rgba(0, 0, 0, 0.55) 100%)`;

  const topBorderRadius = style === 'vintage' ? '6px 6px 0 0' : 
                          style === 'journal' ? '4px 4px 0 0' : 
                          style === 'gothic' ? '8px 8px 0 0' : '2px 2px 0 0';

  const containerStyle = {
    position: 'relative',
    height,
    width,
    background: gradientBg,
    borderRadius: `${topBorderRadius} 0 0`,
    border: '1px solid rgba(0,0,0,0.25)',
    boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.2), 2px 2px 5px rgba(0,0,0,0.2)',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: titleAlign === 'center' ? 'center' : 'flex-start',
    paddingTop: style === 'journal' ? '4px' : '10px',
    paddingBottom: '8px',
    boxSizing: 'border-box',
    overflow: 'visible',
    flexShrink: 0
  };

  return (
    <div
      onClick={onClick}
      title={`${book.title} by ${book.author}`}
      className="cozy-book-spine"
      style={containerStyle}
    >
      {/* Dark depth line at the bottom of the book */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: 'rgba(0,0,0,0.45)'
      }} />

      {/* Structured horizontal ridges or bookmarks */}
      {getSpineDecoration(spineProp)}

      {/* Spine Inscribed Title */}
      {getInscribedTitle(book, spineProp)}

      {/* Embossed Logo Accent */}
      {getEmbossingLogo(spineProp)}
    </div>
  );
};

// --- Quote Scroll Component ---
const QuoteScroll = ({ log }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={log.notes}
      style={{
        position: 'relative',
        width: '28px',
        height: '110px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'flex-end',
        cursor: 'default',
        alignSelf: 'flex-end',
      }}
    >
      <svg width="28" height="110" viewBox="0 0 28 110" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={`scroll-grad-${log.id}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#c8a96e" />
            <stop offset="40%" stopColor="#f5dfa0" />
            <stop offset="100%" stopColor="#c8a96e" />
          </linearGradient>
        </defs>
        {/* Scroll body */}
        <rect x="4" y="10" width="20" height="90" rx="2" fill={`url(#scroll-grad-${log.id})`} />
        {/* Top curl */}
        <ellipse cx="14" cy="10" rx="10" ry="5" fill="#b8924a" />
        <ellipse cx="14" cy="10" rx="8" ry="3.5" fill="#f5dfa0" />
        {/* Bottom curl */}
        <ellipse cx="14" cy="100" rx="10" ry="5" fill="#b8924a" />
        <ellipse cx="14" cy="100" rx="8" ry="3.5" fill="#f5dfa0" />
        {/* Decorative lines */}
        <line x1="8" y1="25" x2="20" y2="25" stroke="#c8a96e" strokeWidth="0.8" opacity="0.6" />
        <line x1="8" y1="35" x2="20" y2="35" stroke="#c8a96e" strokeWidth="0.8" opacity="0.6" />
        <line x1="8" y1="45" x2="20" y2="45" stroke="#c8a96e" strokeWidth="0.8" opacity="0.6" />
        <line x1="8" y1="55" x2="20" y2="55" stroke="#c8a96e" strokeWidth="0.8" opacity="0.6" />
        <line x1="8" y1="65" x2="20" y2="65" stroke="#c8a96e" strokeWidth="0.8" opacity="0.6" />
        <line x1="8" y1="75" x2="20" y2="75" stroke="#c8a96e" strokeWidth="0.8" opacity="0.6" />
        {/* Seal dot */}
        <circle cx="14" cy="55" r="4" fill="#8B4513" opacity="0.5" />
        <circle cx="14" cy="55" r="2.5" fill="#a0522d" opacity="0.7" />
      </svg>
      {/* Hover tooltip */}
      {hovered && (
        <div style={{
          position: 'absolute',
          bottom: '115px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#2c1a0e',
          color: '#f5dfa0',
          fontSize: '0.72rem',
          fontStyle: 'italic',
          padding: '8px 10px',
          borderRadius: '8px',
          width: '180px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          zIndex: 100,
          lineHeight: '1.4',
          border: '1px solid #8B4513',
          pointerEvents: 'none',
          whiteSpace: 'normal',
        }}>
          📜 "{log.notes}"
          <div style={{ fontSize: '0.65rem', color: '#c8a96e', marginTop: '4px', fontStyle: 'normal' }}>
            {log.title} · {log.date}
          </div>
        </div>
      )}
    </div>
  );
};

const SHELF_THEME_STORAGE_KEY = 'll_bookshelf_theme';

const Bookshelf = React.forwardRef(({ books, onBookClick }, snapshotRef) => {
  const { user } = useAuth();
  const { books: allBooks } = useBooks();
  const activeReadingBook = allBooks.find(b => b.status === 'reading');
  const activeVibe = activeReadingBook?.vibe;

  const [scrollLogs, setScrollLogs] = useState([]);
  const [isDecorMode, setIsDecorMode] = useState(false);
  const [activeSlotPicker, setActiveSlotPicker] = useState(null);
  const [unlockedDecor, setUnlockedDecor] = useState([]);
  const [placedDecor, setPlacedDecor] = useState(() => {
    try {
      const saved = localStorage.getItem('local_shelf_decor_layout');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const [shelfThemeKey, setShelfThemeKey] = useState(() => {
    const saved = localStorage.getItem(SHELF_THEME_STORAGE_KEY);
    return WOOD_THEMES[saved] ? saved : DEFAULT_WOOD_THEME_KEY;
  });
  const [showShelfThemePicker, setShowShelfThemePicker] = useState(false);
  const shelfTheme = WOOD_THEMES[shelfThemeKey];

  const pickShelfTheme = (key) => {
    setShelfThemeKey(key);
    localStorage.setItem(SHELF_THEME_STORAGE_KEY, key);
  };

  // Dynamic Google Font Injection for classic and modern typography
  useEffect(() => {
    if (!document.getElementById('cozy-spines-font-link')) {
      const link = document.createElement('link');
      link.id = 'cozy-spines-font-link';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400..900;1,400..900&family=Outfit:wght@100..900&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchUnlocked = async () => {
      try {
        const { data } = await supabase
          .from('user_decor_inventory')
          .select('*, decor_items(*)')
          .eq('user_id', user.id);
        if (data) {
          setUnlockedDecor(data);
        }
      } catch (err) {
        console.error("Failed to load user shelf decor inventory:", err);
      }
    };
    fetchUnlocked();
  }, [user]);

  useEffect(() => {
    localStorage.setItem('local_shelf_decor_layout', JSON.stringify(placedDecor));
  }, [placedDecor]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('reading_logs')
      .select('id, notes, title, date')
      .eq('user_id', user.id)
      .not('notes', 'is', null)
      .order('date', { ascending: false })
      .limit(20)
      .then(({ data }) => setScrollLogs(data || []));
  }, [user]);

  const handlePlaceDecor = (slotKey, item) => {
    setPlacedDecor(prev => ({
      ...prev,
      [slotKey]: item
    }));
    setActiveSlotPicker(null);
    window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: `Placed ${item.emoji} on your shelf! 🛋️`, type: 'success' } }));
  };

  const handleClearSlot = (slotKey) => {
    setPlacedDecor(prev => {
      const copy = { ...prev };
      delete copy[slotKey];
      return copy;
    });
    window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: `Cleared shelf slot.`, type: 'info' } }));
  };

  const slots = [
    { key: 'top-left', style: { position: 'absolute', top: '-34px', left: '15px', zIndex: 10 } },
    { key: 'top-center', style: { position: 'absolute', top: '-34px', left: '50%', transform: 'translateX(-50%)', zIndex: 10 } },
    { key: 'top-right', style: { position: 'absolute', top: '-34px', right: '15px', zIndex: 10 } },
    { key: 'shelf-left', style: { position: 'absolute', bottom: '15px', left: '10px', zIndex: 10 } },
    { key: 'shelf-right', style: { position: 'absolute', bottom: '15px', right: '10px', zIndex: 10 } }
  ];

  return (
    <div style={{ position: 'relative' }}>
      {/* Hover Micro-Animation Styling Tag */}
      <style>
        {`
          .cozy-book-spine {
            transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.2s, filter 0.2s;
          }
          .cozy-book-spine:hover {
            transform: translateY(-8px) scale(1.03) rotate(-1deg);
            box-shadow: 4px 6px 15px rgba(0,0,0,0.35);
            filter: brightness(1.12);
          }
        `}
      </style>

      {/* Decor Mode Toggle Bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <button
          onClick={() => setShowShelfThemePicker(!showShelfThemePicker)}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            borderRadius: '12px',
            border: '1px solid var(--border)',
            backgroundColor: 'var(--bg-card)',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
          title="Change shelf color"
          aria-label="Change shelf color"
        >
          <Palette size={14} style={{ color: 'var(--text-soft)' }} />
        </button>
        <button
          onClick={() => {
            setIsDecorMode(!isDecorMode);
            setActiveSlotPicker(null);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '12px',
            border: '1px solid ' + (isDecorMode ? 'var(--accent)' : 'var(--border)'),
            backgroundColor: isDecorMode ? 'var(--accent-soft)' : 'var(--bg-card)',
            color: isDecorMode ? 'var(--accent)' : 'var(--text-soft)',
            fontWeight: 'bold',
            fontSize: '0.75rem',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          {isDecorMode ? '✨ Done Decorating' : '🛋️ Decorate Bookshelf'}
        </button>
      </div>

      {showShelfThemePicker && (
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginBottom: '10px' }}>
          {Object.entries(WOOD_THEMES).map(([key, t]) => (
            <button
              key={key}
              onClick={() => pickShelfTheme(key)}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: t.surface,
                outline: key === shelfThemeKey ? '2px solid var(--accent)' : '1px solid var(--border)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
              title={t.label}
              aria-label={`${t.label} shelf`}
            />
          ))}
        </div>
      )}

      <div ref={snapshotRef} style={{ ...caseStyle, backgroundColor: shelfTheme.surface, position: 'relative' }}>
        {/* Weather Overlay Effects */}
        {activeVibe === 'melancholy' && <RainOverlay />}
        {activeVibe === 'cozy' && <CozyOverlay />}
        {activeVibe === 'mysterious' && <MistOverlay />}
        {activeVibe === 'thrilling' && <ThrillingOverlay />}

        {/* Decoration Slots */}
        {slots.map(slot => {
          const item = placedDecor[slot.key];
          const isPickerOpen = activeSlotPicker === slot.key;

          return (
            <div key={slot.key} style={slot.style}>
              {item ? (
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '2rem', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }} title={item.name}>
                    {item.emoji}
                  </span>
                  {isDecorMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearSlot(slot.key);
                      }}
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-6px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#ff5252',
                        color: 'white',
                        border: '1px solid white',
                        fontSize: '0.65rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        padding: 0
                      }}
                      title="Remove item"
                    >
                      ×
                    </button>
                  )}
                </div>
              ) : (
                isDecorMode && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveSlotPicker(isPickerOpen ? null : slot.key);
                    }}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      border: '1.5px dashed var(--accent)',
                      backgroundColor: isPickerOpen ? 'var(--accent-soft)' : 'rgba(255, 255, 255, 0.75)',
                      color: 'var(--accent)',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s',
                      padding: 0,
                      outline: 'none'
                    }}
                    title="Add Shelf Decor"
                  >
                    +
                  </button>
                )
              )}
            </div>
          );
        })}

        <div style={{ ...shelfStyle, borderBottomColor: shelfTheme.drawer }}>
          {books.map((book) => (
            <CozyBookSpine
              key={book.id}
              book={book}
              onClick={() => onBookClick && onBookClick(book)}
            />
          ))}
          {scrollLogs.map((log) => (
            <QuoteScroll key={log.id} log={log} />
          ))}
        </div>
      </div>

      {/* Shelf Decor Picker Drawer */}
      {isDecorMode && activeSlotPicker && (
        <div 
          style={{
            marginTop: '15px',
            padding: '15px',
            backgroundColor: 'var(--bg-card)',
            borderRadius: '16px',
            border: '1px solid var(--border)',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-soft)', textTransform: 'uppercase' }}>
              Select Decor for {activeSlotPicker.replace('-', ' ')}
            </span>
            <button 
              onClick={() => setActiveSlotPicker(null)}
              style={{ background: 'none', border: 'none', color: 'var(--text-soft)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' }}
            >
              Cancel
            </button>
          </div>
          
          <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '5px' }}>
            {[
              { emoji: '🪴', name: 'Fern' },
              { emoji: '🕯️', name: 'Candle' },
              { emoji: '🫖', name: 'Teapot' },
              { emoji: '🐱', name: 'Cat' },
              { emoji: '☕', name: 'Mug' },
              { emoji: '🦉', name: 'Owl' },
              { emoji: '📚', name: 'Books' },
              { emoji: '🌵', name: 'Succulent' },
              { emoji: '🏆', name: 'Trophy' }
            ].map(item => (
              <button
                key={item.name}
                onClick={() => handlePlaceDecor(activeSlotPicker, item)}
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px 12px',
                  backgroundColor: 'var(--bg-app)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  minWidth: '64px',
                  transition: 'all 0.2s'
                }}
              >
                <span style={{ fontSize: '1.4rem' }}>{item.emoji}</span>
                <span style={{ fontSize: '0.6rem', color: 'var(--text-soft)', fontWeight: 'bold' }}>{item.name}</span>
              </button>
            ))}

            {unlockedDecor.map(item => {
              const emoji = getDecorEmoji(item.decor_items?.name);
              const name = item.decor_items?.name || 'Unlocked Item';
              return (
                <button
                  key={item.id}
                  onClick={() => handlePlaceDecor(activeSlotPicker, { emoji, name })}
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 12px',
                    backgroundColor: 'var(--accent-soft)',
                    border: '1px solid var(--accent)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    minWidth: '64px',
                    transition: 'all 0.2s'
                  }}
                >
                  <span style={{ fontSize: '1.4rem' }}>{emoji}</span>
                  <span style={{ fontSize: '0.6rem', color: 'var(--accent)', fontWeight: 'bold' }}>{name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

const caseStyle = {
  backgroundColor: '#5D4037', // Dark wood color
  padding: '20px 20px 0 20px',
  borderRadius: '8px 8px 0 0',
  boxShadow: 'inset 0 0 20px rgba(0,0,0,0.3)',
  minHeight: '220px',
  display: 'flex',
  flexDirection: 'column',
  gap: '0px',
  marginTop: '2rem'
};

// Books wrap onto additional shelf rows instead of scrolling off-screen
// horizontally with no visual cue that more books exist.
const shelfStyle = {
  borderBottom: '15px solid #3E2723', // Shelf board
  width: '100%',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  gap: '2px 2px', // Space between books, and between wrapped rows
  paddingBottom: '0px',
  rowGap: '10px',
  minHeight: '200px',
  zIndex: 2 // Make sure books are clickable above absolute overlays
};

const spineStyle = {
  borderRadius: '2px 2px 0 0',
  border: '1px solid rgba(0,0,0,0.1)',
  boxShadow: 'inset 2px 0 5px rgba(255,255,255,0.2), inset -2px 0 5px rgba(0,0,0,0.1)',
  cursor: 'pointer',
  position: 'relative',
  transition: 'transform 0.2s',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  writingMode: 'vertical-rl', // Vertical text
  transform: 'rotate(180deg)', // Flip so text reads bottom-to-top
};

const spineTextStyle = {
  fontSize: '0.6rem',
  color: 'rgba(255,255,255,0.8)',
  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  pointerEvents: 'none' // Clicks go to parent div
};

// Weather Styling Constants
const weatherOverlayStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 1,
  borderRadius: '8px 8px 0 0',
  overflow: 'hidden'
};

const sunbeamStyle = {
  position: 'absolute',
  top: '-50px',
  left: '15%',
  width: '120px',
  height: '350px',
  background: 'linear-gradient(135deg, rgba(255, 236, 179, 0.18) 0%, rgba(255, 236, 179, 0) 70%)',
  transform: 'rotate(25deg)',
  pointerEvents: 'none'
};

export default Bookshelf;
